import { create } from "zustand";
import { apiFetch, API_BASE_URL, ApiError } from "@/lib/api";
import type { User } from "@/types/api";

const RETURN_URL_KEY = "auth_return_url";

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  fetchUser: () => Promise<void>;
  logout: () => Promise<void>;
  loginWithWhop: (returnUrl?: string) => void;
  /** Optimistically update user preferences (for immediate UI feedback) */
  updateUserOptimistic: (updates: Partial<User>) => void;
}

// Get and clear the stored return URL
export function getAndClearReturnUrl(): string | null {
  if (typeof window === "undefined") return null;
  const url = localStorage.getItem(RETURN_URL_KEY);
  if (url) {
    localStorage.removeItem(RETURN_URL_KEY);
  }
  return url;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,

  fetchUser: async () => {
    try {
      const user = await apiFetch<User>("/auth/me");
      set({ user, isAuthenticated: true, isLoading: false });
    } catch (error) {
      // Not authenticated or error - that's fine
      set({ user: null, isAuthenticated: false, isLoading: false });

      // Only log non-401 errors (401 is expected when not logged in)
      if (error instanceof ApiError && error.status !== 401) {
        console.error("Error fetching user:", error);
      }
    }
  },

  logout: async () => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } catch {
      // Ignore errors - we're logging out anyway
    }
    set({ user: null, isAuthenticated: false });
  },

  loginWithWhop: (returnUrl?: string) => {
    // SSR guard - this function uses browser APIs
    if (typeof window === "undefined") return;
    // Save return URL to localStorage before redirecting to OAuth
    if (returnUrl) {
      localStorage.setItem(RETURN_URL_KEY, returnUrl);
    }
    window.location.href = `${API_BASE_URL}/auth/whop`;
  },

  updateUserOptimistic: (updates) => {
    set((state) => ({
      user: state.user ? { ...state.user, ...updates } : null,
    }));
  },
}));
